import React, { useEffect, useState } from 'react';

import { getCookie } from 'common/utils/cookies';
import { useAppSelector } from 'components/app/redux/store';

import {
  useAskMutation,
  useReportErrorMutation,
  useVoiceMutation,
} from './api/agent.api';
import { Dialog } from './components/dialog';
import { MessageRecord, Participant } from './types/message';

export const AIAgent: React.FC = () => {
  const [sessionId, setSessionId] = useState<string>('');
  const { initialMessage, errorReportSend, connectionTimeoutError } =
    useAppSelector(state => state.localization.components.ChatBot.messages);

  const [messagesHistory, updateMessagesHistory] = useState<
    Array<MessageRecord>
  >([
    {
      content: initialMessage,
      owner: Participant.Agent,
    },
  ]);

  const [ask, { isLoading }] = useAskMutation();
  const [voice] = useVoiceMutation();
  const [reportError, {}] = useReportErrorMutation();
  const [inputText, setInputText] = useState<string>('');

  useEffect(() => {
    const sessionCookie = getCookie('_session_id');
    if (sessionCookie) {
      setSessionId(sessionCookie);
    } else {
      setSessionId(`temp_${Math.random().toString(36).substring(2, 9)}`);
    }
  }, []);

  const pushMessage = (message: MessageRecord) => {
    updateMessagesHistory(history => [message, ...history]);
  };

  const parseAIResponse = response => {
    try {
      const parsedResponse = JSON.parse(response);
      if (parsedResponse.endpoint) {
        const newParams = Object.entries(parsedResponse.params)
          .map(([key, value]) => `${key}=${value}`)
          .join('&');
        const linkUrl = `${window.location.origin}/${parsedResponse.endpoint}${newParams ? `?${newParams}` : ''}`;
        window.open(linkUrl, '_blank', 'noopener,noreferrer');
        return {
          type: 'link',
          content: linkUrl,
        };
      }
      return {
        type: 'text',
        content: response,
      };
    } catch (e) {
      return {
        type: 'text',
        content: response,
      };
    }
  };

  const handleTextRequest = async (question: string) => {
    pushMessage({
      owner: Participant.User,
      content: question,
    });
    const accessToken = getCookie('access_token');
    try {
      const data = await ask({
        input: question,
        session_id: sessionId,
        token: accessToken,
      }).unwrap();
      const parsedContent = parseAIResponse(data.response);
      pushMessage({ owner: Participant.Agent, ...parsedContent });
    } catch (error) {
      if (error.status === 403) {
        pushMessage({
          owner: Participant.Agent,
          type: 'text',
          content: error.data.response,
        });
      }
      if (error.status === 'TIMEOUT_ERROR') {
        pushMessage({
          owner: Participant.Agent,
          type: 'text',
          content: connectionTimeoutError,
        });
      }
      console.error(error);
    }
  };

  const handleVoiceRequest = async (blob: Blob) => {
    // Create object URL from blob
    const audioUrl = URL.createObjectURL(blob);

    // Create audio element and play
    const audio = new Audio(audioUrl);
    //audio.play().catch(error => {
    //  console.error('Error playing audio:', error);
    //});

    const form = new FormData();
    form.append('audio', blob);
    try {
      const data = await voice({
        file: blob,
      }).unwrap();
      setInputText(data.text);
      //pushMessage({ owner: Participant.User, content: data.text });
    } catch (error) {
      console.error(error);
    }

    // Clean up URL when done (optional but recommended)
    audio.onended = () => {
      URL.revokeObjectURL(audioUrl);
    };
  };

  const handleErrorReportRequest = async () => {
    updateMessagesHistory(history => {
      return [
        { owner: Participant.Agent, content: errorReportSend },
        ...history,
      ];
    });
    await reportError({
      request: messagesHistory.map(item => ({
        text: item.content,
        isUser: item.owner === Participant.User,
      })),
    });
  };

  return (
    <Dialog
      history={messagesHistory}
      loading={isLoading}
      onErrorReportRequest={handleErrorReportRequest}
      onTextRequest={handleTextRequest}
      onVoiceRequest={handleVoiceRequest}
      responseText={inputText}
    />
  );
};
